// RichProfileDetailModal — admin-style profile detail modal that other
// role-end pages (currently /profiles for leader_coach / coach / admin) can
// open inline instead of navigating away. Visually mirrors the inline modal
// rendered on /admin/profiles: gallery on the left, scrollable details on
// the right (name + basic chips, bio, contact + assignment, custom fields
// grouped by category, status badge, footer with Close + Edit).
//
// Contact info (email + phone) is role-gated — only admin / leader_coach /
// coach see it; supervisor / interviewer / matchmaker get a single-column
// layout without it.

import React, { useEffect, useState } from 'react';
import { profilesAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { isInvalidPhotoUrl, resolvePhotoUrl } from '../../utils/photoUtils';
import { translateOptionLabel, appendUnit } from '../../utils/prefValueI18n';
import { translateLocation } from '../../data/locationTranslations';
import {
  X as XIcon, Cake, MapPin, Briefcase, Star, Mail, Phone, User, Target,
  Edit2, Heart, ClipboardCheck, ClipboardList, Ticket, Layers,
  Eye, EyeOff, Image as ImageIcon, FileText,
  AlertTriangle, Check, Circle, ChevronLeft, ChevronRight,
} from 'lucide-react';
import PhotoAlbumTab from './PhotoAlbumTab';
import RecStatusTag from './RecStatusTag';

const STATUS_LABEL_ZH = {
  active: '活跃', pause: '暂停服务', paused: '暂停服务',
  in_relationship: '交往中', service_ended: '服务终止',
  archived: '已归档', inactive: '已停用',
};
const STATUS_COLOR = {
  active:          { bg: 'rgba(63,125,90,.12)', fg: '#3F7D5A' },
  pause:           { bg: 'rgba(147,89,26,.10)', fg: '#93591A' },
  paused:          { bg: 'rgba(147,89,26,.10)', fg: '#93591A' },
  in_relationship: { bg: 'rgba(138,109,59,.12)', fg: '#8A6D3B' },
  service_ended:   { bg: 'rgba(17,17,17,.12)', fg: '#3A3835' },
  inactive:        { bg: 'rgba(158,43,37,.09)', fg: '#9E2B25' },
  archived:        { bg: 'rgba(158,43,37,.09)', fg: '#9E2B25' },
};

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleDateString('zh-CN');
};
const fmtDateTime = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleString('zh-CN', { hour12: false });
};
const followupActorName = (row) => {
  const fn = (row.actor_first_name || '').trim();
  const ln = (row.actor_last_name || '').trim();
  const name = `${fn} ${ln}`.trim();
  if (name) return name;
  if (row.actor_label) return row.actor_label;
  if (row.actor_type === 'client') return '客户';
  if (row.actor_type === 'ai') return 'AI';
  if (row.actor_type === 'system') return '系统';
  return '员工';
};

// Recursive value normalizer for custom-field values (handles JSON strings,
// range {start,end} objects, arrays, and {ideal/acceptable/bottom_line}
// tier objects). Returns '' (not '[object Object]') for unknown shapes.
const normalizeAny = (v) => {
  if (v == null || v === '') return '';
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (trimmed === '[object Object]') return '';
    if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && (trimmed.endsWith('}') || trimmed.endsWith(']'))) {
      try { return normalizeAny(JSON.parse(trimmed)); } catch { return trimmed; }
    }
    return trimmed;
  }
  if (Array.isArray(v)) return v.map(normalizeAny).filter(Boolean).join('、');
  if (typeof v === 'object') {
    if ('start' in v || 'end' in v) return `${v.start ?? ''} ~ ${v.end ?? ''}`.trim().replace(/^~\s*|\s*~$/g, '');
    if ('min' in v || 'max' in v) return `${v.min ?? ''} ~ ${v.max ?? ''}`.trim().replace(/^~\s*|\s*~$/g, '');
    if ('ideal' in v || 'acceptable' in v || 'bottom_line' in v) {
      const parts = [];
      const idealStr = normalizeAny(v.ideal);
      const accStr   = normalizeAny(v.acceptable);
      const blStr    = normalizeAny(v.bottom_line);
      if (idealStr) parts.push(`理想: ${idealStr}`);
      if (accStr)   parts.push(`可接受: ${accStr}`);
      if (blStr)    parts.push(`底线: ${blStr}`);
      return parts.join('; ');
    }
    return '';
  }
  return String(v);
};

// Translate an option value via field_options metadata (each option:
// {en, zh} or {value, label_zh} etc). Returns the raw value when no match.
const translateOption = (raw, opts) => {
  if (raw == null || raw === '') return '';
  let parsed = opts;
  if (typeof parsed === 'string') { try { parsed = JSON.parse(parsed); } catch { parsed = null; } }
  if (!Array.isArray(parsed) || parsed.length === 0) return translateOptionLabel(String(raw), 'zh');
  const sval = String(raw).trim();
  const match = parsed.find(o => {
    if (o == null) return false;
    if (typeof o === 'string') return o === sval;
    return o.en === sval || o.value === sval || o.key === sval || o.label === sval || o.zh === sval;
  });
  // No field_options match → fall back to the shared EN→中文 dictionary so
  // values like Unmarried / Divorced / Sports / Mandarin still translate.
  if (!match) return translateOptionLabel(String(raw), 'zh');
  if (typeof match === 'string') return match;
  return match.zh || match.label_zh || match.label || match.en || match.value || match.key || String(raw);
};

// Group customFields by category, normalizing each value for display.
const groupByCategory = (customFields) => {
  const groups = {};
  (customFields || []).forEach(cf => {
    if (!cf) return;
    // Hide the WeChat name field (weixin / id 138).
    if ((cf.field_name || '').toLowerCase() === 'weixin' || String(cf.custom_field_id || cf.id) === '138') return;
    const category = (cf.category || 'other').toLowerCase();
    let display = normalizeAny(cf.field_value);
    // Run option translation on commas-joined arrays as well as single values.
    if (display && cf.field_options) {
      const parts = display.split(/,\s*|，\s*|、/).map(s => s.trim()).filter(Boolean);
      if (parts.length > 0) {
        display = parts.map(p => translateOption(p, cf.field_options)).join('、');
      }
    }
    if (!display && category !== 'preferences') return;
    if (!groups[category]) groups[category] = [];
    const label = (cf.field_label_zh || cf.field_label || cf.field_name || '').trim();
    if (!label) return;
    groups[category].push({ label, value: appendUnit(label, cf.field_name, display) || '-' });
  });
  return groups;
};

const CATEGORY_LABEL_ZH = {
  general:     '基本资料',
  personal:    '个人信息',
  preferences: '择偶要求',
  lifestyle:   '生活方式',
  background:  '背景资料',
  other:       '其他',
};

// ── Mate-preference three-dimension display ──────────────────────────────
// Mirrors the /admin profile detail's 择偶要求（三维度）view: each preference
// field decodes into three tiers (理想 / 可接受 / 底线), color-badged, all in
// Chinese. Empty tiers render "—" so blanks are visible at a glance.
const TIER_DISPLAY = [
  { key: 'ideal',       label: '理想',  color: 'var(--color-gold)' },
  { key: 'acceptable',  label: '可接受', color: '#3F7D5A' },
  { key: 'bottom_line', label: '底线',  color: '#9E2B25' },
];

const MATE_PREF_LABELS = {
  mate_age: '理想年龄',
  mate_heigh: '理想身高',
  mate_height: '理想身高',
  mate_pay: '理想收入',
  mate_love: '婚姻状况',
  mate_child: '生育意愿',
  mate_house: '房产要求',
  mate_education: '学历要求',
  mate_marriage_intent: '婚恋目的',
};

const labelOfField = (cf) => {
  if (!cf) return '';
  const fname = (cf.field_name || '').toLowerCase();
  return (cf.field_label_zh || MATE_PREF_LABELS[fname] || cf.field_label || cf.field_name || '').trim();
};

// Decode one tier sub-value to a Chinese display string, applying the field's
// option metadata (reuses the same normalize + translate helpers used by the
// category list above).
const tierValueText = (raw, opts) => {
  let s = normalizeAny(raw);
  if (s && opts) {
    const parts = s.split(/,\s*|，\s*|、/).map(x => x.trim()).filter(Boolean);
    if (parts.length) s = parts.map(x => translateOption(x, opts)).join('、');
  }
  return s;
};

// Returns the three tier rows for a mate-pref field, or a single flat row when
// the value isn't a {ideal/acceptable/bottom_line} object.
const tiersOf = (cf) => {
  let raw = cf.field_value;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (s.startsWith('{') || s.startsWith('[')) { try { raw = JSON.parse(s); } catch { /* keep */ } }
  }
  const unitLabel = `${labelOfField(cf)} ${cf.field_name || ''}`;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)
      && ('ideal' in raw || 'acceptable' in raw || 'bottom_line' in raw)) {
    return TIER_DISPLAY.map(t => {
      const v = tierValueText(raw[t.key], cf.field_options);
      return { label: t.label, color: t.color, value: appendUnit(unitLabel, cf.field_name, v) || '—', empty: !v };
    });
  }
  // Not a tier object (saved before 三维, most older profiles) — still show the three tiers:
  // the value as 可接受, the other two —.
  const flat = tierValueText(raw, cf.field_options);
  return flat ? TIER_DISPLAY.map(t => (t.key === 'acceptable'
    ? { label: t.label, color: t.color, value: appendUnit(unitLabel, cf.field_name, flat), empty: false }
    : { label: t.label, color: t.color, value: '—', empty: true })) : [];
};

function Gallery({ photos, initials, blurred = false }) {
  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const visible = (photos || []).filter(p => p && !isInvalidPhotoUrl(p));
  if (visible.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', background: '#EFEAE2', color: '#8A6D3B', fontFamily: "'Cormorant Garamond', 'Noto Serif SC', Georgia, serif", fontSize: 64, fontWeight: 500 }}>
        {initials}
      </div>
    );
  }
  // When photos are hidden (show_photos = off) we blur them and suppress the
  // lightbox so the actual image can't be opened full-res.
  const blurStyle = blurred ? { filter: 'blur(22px)', transform: 'scale(1.08)' } : {};
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', background: '#EFEAE2', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      <img src={resolvePhotoUrl(visible[idx])} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: blurred ? 'default' : 'pointer', ...blurStyle }} onClick={() => { if (!blurred) setLightbox(true); }} />
      {blurred && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#FFFDF9', textShadow: '0 1px 4px rgba(0,0,0,0.6)', pointerEvents: 'none' }}>
          <EyeOff size={30} />
          <span style={{ fontSize: 13, fontWeight: 400 }}>照片已隐藏</span>
        </div>
      )}
      {visible.length > 1 && (
        <>
          <button onClick={() => setIdx((idx - 1 + visible.length) % visible.length)} style={navBtnStyle('left')} aria-label="上一张"><ChevronLeft size={20} strokeWidth={1.4} aria-hidden="true" /></button>
          <button onClick={() => setIdx((idx + 1) % visible.length)} style={navBtnStyle('right')} aria-label="下一张"><ChevronRight size={20} strokeWidth={1.4} aria-hidden="true" /></button>
          <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 6 }}>
            {visible.map((_, i) => (
              <div key={i} onClick={() => setIdx(i)}
                style={{ width: i === idx ? 20 : 8, height: 8, borderRadius: 2, background: i === idx ? 'var(--color-burgundy)' : 'rgba(255,255,255,0.7)', cursor: 'pointer', transition: 'all .2s' }} />
            ))}
          </div>
        </>
      )}
      <div style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(0,0,0,0.5)', color: '#FFFDF9', padding: '4px 10px', borderRadius: 2, fontSize: 12, fontWeight: 400 }}>
        {idx + 1} / {visible.length}
      </div>
      {lightbox && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 31500, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setLightbox(false)}>
          <img src={resolvePhotoUrl(visible[idx])} alt="" style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain', borderRadius: 2 }} />
          <button onClick={() => setLightbox(false)} style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#FFFDF9', fontSize: 28, width: 44, height: 44, borderRadius: '50%', cursor: 'pointer' }}>×</button>
        </div>
      )}
    </div>
  );
}
const navBtnStyle = (side) => ({
  position: 'absolute', [side]: 8, top: '50%', transform: 'translateY(-50%)',
  width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.85)',
  border: 'none', cursor: 'pointer', fontSize: 18, display: 'flex',
  alignItems: 'center', justifyContent: 'center', boxShadow: 'none',
});

// Pill toggle used for 照片 / 资料 公开/隐藏 on the gallery. Green = public,
// muted = hidden, with a sliding knob so the on/off state reads at a glance.
function VisibilityToggle({ on, disabled, onToggle, icon, labelOn, labelOff }) {
  return (
    <button type="button" onClick={onToggle} disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '5px 10px 5px 8px', borderRadius: 2, border: 'none',
        cursor: disabled ? 'wait' : 'pointer', fontFamily: 'inherit',
        background: on ? 'rgba(22,163,74,0.92)' : 'rgba(31,41,55,0.78)',
        color: '#FFFDF9', fontSize: 12, fontWeight: 400, backdropFilter: 'blur(4px)',
        boxShadow: 'none', whiteSpace: 'nowrap',
      }}>
      <span style={{ display: 'inline-flex' }}>{icon}</span>
      <span>{on ? labelOn : labelOff}</span>
      <span style={{
        position: 'relative', width: 26, height: 15, borderRadius: 2, flexShrink: 0,
        background: on ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.3)', transition: 'all .15s',
      }}>
        <span style={{
          position: 'absolute', top: 2, left: on ? 13 : 2, width: 11, height: 11,
          borderRadius: '50%', background: '#FFFDF9', transition: 'left .15s',
        }} />
      </span>
    </button>
  );
}

export default function RichProfileDetailModal({ profileId, onClose, contactAdminOnly = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = String(user?.role || user?.role_name || '').toLowerCase();
  const _rolesArr = Array.isArray(user?.roles) ? user.roles.map(r => (r || '').toLowerCase()) : [];
  const isAdmin       = ['admin', 'super_admin'].includes(role)
                        || _rolesArr.some(r => ['admin', 'super_admin'].includes(r));
  // Editor role: can edit ANY profile (not just own clients), like admin.
  const isEditor      = role === 'editor' || _rolesArr.includes('editor');
  const isSupervisor  = role === 'supervisor_coach' || role === 'supervisor';
  const isCoachRole   = ['coach', 'leader_coach'].includes(role) || _rolesArr.some(r => ['coach', 'leader_coach'].includes(r));

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  // 跟进记录 — every kpi_v2_ticket_events row of type 'followup' across all
  // tickets where this profile is the client. Aggregated by the existing
  // /api/profiles/:id/followups endpoint, newest first.
  const [followups, setFollowups] = useState([]);
  const [followupsLoading, setFollowupsLoading] = useState(false);
  // Tabbed detail view: 基本资料 / 择偶要求 / 服务协议 / 跟进记录.
  const [activeTab, setActiveTab] = useState('basic');
  // Privacy toggles — 照片公开/隐藏 (show_photos) + 资料公开/隐藏 (is_public).
  // Local mirror of the persisted columns so the modal reflects changes
  // instantly; each toggle persists via PATCH /profiles/:id/visibility.
  const [showPhotos, setShowPhotos] = useState(true);
  const [isPublic, setIsPublic] = useState(true);
  const [savingVis, setSavingVis] = useState(false);

  const toggleVisibility = async (key) => {
    if (savingVis || !profileId) return;
    const next = key === 'show_photos' ? !showPhotos : !isPublic;
    // Optimistic flip.
    if (key === 'show_photos') setShowPhotos(next); else setIsPublic(next);
    setSavingVis(true);
    try {
      await profilesAPI.patchVisibility(profileId, { [key]: next });
      setProfile(prev => prev ? { ...prev, [key]: next ? 1 : 0 } : prev);
    } catch (e) {
      // Revert on failure.
      if (key === 'show_photos') setShowPhotos(!next); else setIsPublic(!next);
    } finally {
      setSavingVis(false);
    }
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!profileId) return;
    let alive = true;
    setLoading(true); setError('');
    profilesAPI.getById(profileId)
      .then(r => {
        if (!alive) return;
        const prof = r.data?.profile || r.data;
        setProfile(prof);
        // show_photos defaults to ON (1), is_public defaults to OFF (0) in the DB.
        setShowPhotos(prof?.show_photos == null ? true : (prof.show_photos === true || prof.show_photos === 1 || prof.show_photos === '1'));
        setIsPublic(prof?.is_public === true || prof?.is_public === 1 || prof?.is_public === '1');
      })
      .catch(e => { if (alive) setError(e?.response?.data?.error || '加载失败'); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [profileId]);

  // Pull followups in parallel — endpoint silently returns [] when the
  // kpi_v2_ticket_events table isn't provisioned, so this never errors out.
  useEffect(() => {
    if (!profileId) return;
    let alive = true;
    setFollowupsLoading(true);
    profilesAPI.getFollowups(profileId)
      .then(r => { if (alive) setFollowups(r.data?.followups || []); })
      .catch(() => { if (alive) setFollowups([]); })
      .finally(() => alive && setFollowupsLoading(false));
    return () => { alive = false; };
  }, [profileId]);
  // ESC closes the modal — same UX as /admin/profiles' inline modal.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const p = profile || {};
  // Ownership gate — non-admin viewers see contact info + 编辑 button
  // only when this profile is assigned to them (matchmaker_id /
  // assigned_coach_id / interviewer_id). Admins always see everything.
  // This matters in 会员库 where coaches can browse profiles outside
  // their own client roster.
  const meId = Number(user?.id);
  const isOwner = !!profile && (
    Number(p.matchmaker_id) === meId ||
    Number(p.assigned_coach_id) === meId ||
    Number(p.interviewer_id) === meId
  );
  // Contact info: only admin + coach (leader_coach) may see it. From the
  // 会员库 (member library) it's restricted further to admin only.
  const canSeeContact = contactAdminOnly ? isAdmin : (isAdmin || isCoachRole);
  const canEdit = !isSupervisor && (isAdmin || isEditor || isOwner);
  const firstName = p.first_name || '';
  const lastName  = p.last_name || '';
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  const fullName = `${firstName} ${lastName}`.trim() || '未命名档案';
  const birth = p.birth_date || p.date_of_birth;
  const age = birth ? Math.floor((new Date() - new Date(birth)) / (365.25 * 24 * 60 * 60 * 1000)) : null;
  const tl = (v) => (v ? translateLocation(v, 'zh') : '');
  const city = tl(p.city || p.location_city || '');
  const stateVal = tl(p.state || p.location_state || '');
  const countryVal = tl(p.country || p.location_country || '');
  const locationStr = [city, stateVal, countryVal].filter(Boolean).join('、');
  const galleryRaw = p.gallery || p.gallery_photos;
  let gallery = [];
  try { gallery = typeof galleryRaw === 'string' ? JSON.parse(galleryRaw) : (Array.isArray(galleryRaw) ? galleryRaw : []); } catch { gallery = []; }
  const allPhotos = [p.profile_photo, ...gallery].filter(Boolean);
  const genderRaw = String(p.gender || '').toLowerCase();
  const isFemale = genderRaw === 'female' || p.gender === '女';
  const isMale   = genderRaw === 'male'   || p.gender === '男';
  const genderLabel = isFemale ? '女' : isMale ? '男' : (p.gender || '');
  const statusKey = String(p.status || 'active').toLowerCase();
  const statusLabel = STATUS_LABEL_ZH[statusKey] || p.status || '';
  const statusC = STATUS_COLOR[statusKey] || { bg: '#EFEAE2', fg: '#6E685F' };
  // Offline id surfaced by backend OR from customFields (id 115 fallback)
  const offlineId = p.offline_id
    || (Array.isArray(p.customFields) ? p.customFields.find(c => String(c.custom_field_id) === '115')?.field_value : null)
    || p.id;

  const cfGroups = groupByCategory(p.customFields);
  // 提供身份证件 = 未提供 (provide_photo_id 'No') — surfaced as a banner, not
  // buried as one line among the custom fields.
  const noPhotoId = (Array.isArray(p.customFields) ? p.customFields : []).some(c => {
    if ((c.field_name || '').toLowerCase() !== 'provide_photo_id') return false;
    const v = String(c.field_value ?? c.value ?? '').trim().toLowerCase();
    return v === 'no' || v === '未提供';
  });
  // 择偶要求 is pulled out into its own tab with the three-dimension display,
  // so it's excluded from the 基本资料 category list below.
  const categoryOrder = ['general', 'personal', 'lifestyle', 'background', 'other'];

  // Mate-preference fields (category 'preferences') decoded into tier rows for
  // the 择偶要求 tab's three-dimension display.
  const prefFields = (Array.isArray(p.customFields) ? p.customFields : [])
    .filter(cf => cf && (cf.category || 'other').toLowerCase() === 'preferences')
    .map(cf => ({ label: labelOfField(cf), rows: tiersOf(cf) }))
    .filter(it => it.label && it.rows.length > 0);

  const TABS = [
    { key: 'basic',       label: '基本资料', icon: <User size={14} /> },
    { key: 'preferences', label: '择偶要求', icon: <Layers size={14} /> },
    { key: 'agreement',   label: '服务协议', icon: <ClipboardCheck size={14} /> },
    { key: 'followups',   label: '跟进记录', icon: <ClipboardList size={14} /> },
    { key: 'album',       label: '相册集',   icon: <ImageIcon size={14} /> },
  ];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(17,17,17,0.6)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        // Above the 会员库 overlay (30000) so card-click opens the
        // detail modal on top, not behind it.
        zIndex: 31000, padding: isMobile ? 0 : 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#FFFDF9',
          borderRadius: isMobile ? 0 : 2,
          maxWidth: 1100, width: isMobile ? '100%' : '90%',
          height: isMobile ? '100vh' : '85vh',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          overflow: 'hidden', position: 'relative',
          boxShadow: 'none',
          fontFamily: "'Jost', 'Noto Sans SC', 'PingFang SC', sans-serif",
        }}
      >
        <button onClick={onClose} aria-label="close"
          style={{
            position: 'absolute', top: 12, right: 12, zIndex: 10,
            width: 36, height: 36, borderRadius: '50%',
            background: 'rgba(0,0,0,0.4)', border: 'none', color: '#FFFDF9',
            fontSize: 20, cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)',
          }}>
          <XIcon size={18} />
        </button>

        {/* Left: gallery */}
        <div style={{ width: isMobile ? '100%' : '42%', height: isMobile ? 320 : '100%', flexShrink: 0, overflow: 'hidden', position: 'relative' }}>
          <Gallery photos={allPhotos} initials={initials || '?'} blurred={!showPhotos} />
          {/* Privacy toggles overlaid on the gallery (editors only). */}
          {canEdit && !loading && (
            <div style={{ position: 'absolute', left: 12, bottom: 12, display: 'flex', flexDirection: 'column', gap: 8, zIndex: 5 }}>
              <VisibilityToggle
                on={showPhotos}
                disabled={savingVis}
                onToggle={() => toggleVisibility('show_photos')}
                icon={showPhotos ? <ImageIcon size={13} /> : <EyeOff size={13} />}
                labelOn="照片公开"
                labelOff="照片隐藏"
              />
              <VisibilityToggle
                on={isPublic}
                disabled={savingVis}
                onToggle={() => toggleVisibility('is_public')}
                icon={isPublic ? <Eye size={13} /> : <FileText size={13} />}
                labelOn="资料公开"
                labelOff="资料隐藏"
              />
            </div>
          )}
        </div>

        {/* Right: scrollable details */}
        <div style={{
          flex: 1, overflowY: 'auto',
          padding: isMobile ? '20px 16px 30px' : '28px 32px',
          display: 'flex', flexDirection: 'column',
        }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#6E685F', fontSize: 13 }}>加载中…</div>
          ) : error ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#9E2B25', fontSize: 13 }}>{error}</div>
          ) : (
            <>
              {/* Privacy banner — surfaces when this profile is hidden from
                  public views (is_public off) and/or its photos are hidden. */}
              {(!isPublic || !showPhotos) && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
                  padding: '8px 12px', borderRadius: 2, fontSize: 12, fontWeight: 400,
                  background: 'rgba(147,89,26,.10)', color: '#93591A', border: '1px solid rgba(147,89,26,.25)',
                }}>
                  <EyeOff size={14} />
                  <span>
                    {!isPublic && !showPhotos ? '此档案的资料与照片已隐藏，不对外公开'
                      : !isPublic ? '此档案已隐藏，不对外公开显示'
                      : '此档案照片已隐藏（前端模糊处理）'}
                  </span>
                </div>
              )}

              {noPhotoId && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
                  padding: '8px 12px', borderRadius: 2, fontSize: 12, fontWeight: 400,
                  background: 'rgba(147,89,26,.10)', color: '#93591A', border: '1px solid rgba(147,89,26,.25)',
                }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={14} strokeWidth={1.5} aria-hidden="true" />该会员未提供身份证件</span>
                </div>
              )}

              {/* Header: name + gender + basic chips */}
              <div style={{ marginBottom: 20 }}>
                <h2 style={{ margin: '0 0 6px', fontFamily: "'Cormorant Garamond', 'Noto Serif SC', 'Songti SC', Georgia, serif", fontSize: '1.9rem', fontWeight: 500, lineHeight: 1.15, color: '#111111' }}>
                  {fullName}
                  {genderLabel && (
                    <span style={{
                      display: 'inline-block', marginLeft: 10, padding: '2px 10px',
                      borderRadius: 2, fontSize: '0.75rem', fontWeight: 400,
                      background: isMale ? 'rgba(17,17,17,.06)' : 'rgba(138,109,59,.1)',
                      color:      isMale ? 'rgb(37,99,235)'   : 'rgb(236,73,156)',
                    }}>{genderLabel}</span>
                  )}
                  <RecStatusTag profile={p} style={{ marginLeft: 10, fontFamily: "'Jost', 'Noto Sans SC', 'PingFang SC', sans-serif" }} />
                </h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: '0.88rem', color: '#6E685F', alignItems: 'center' }}>
                  {age != null && (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Cake size={13} /> {age} 岁</span>)}
                  {locationStr && (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MapPin size={13} /> {locationStr}</span>)}
                  {p.occupation && (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Briefcase size={13} /> {p.occupation}</span>)}
                  {p.star_sign && (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Star size={13} /> {translateOptionLabel(p.star_sign, 'zh')}</span>)}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                  {p.plan_name && (
                    <span style={{ padding: '3px 10px', borderRadius: 2, fontSize: '0.75rem', fontWeight: 400, background: '#EFEAE2', color: 'var(--color-burgundy)' }}>
                      {p.plan_name}
                    </span>
                  )}
                  <span style={{ padding: '3px 10px', borderRadius: 2, fontSize: '0.75rem', fontWeight: 400, background: '#EFEAE2', color: '#6E685F' }}>
                    线下ID: {offlineId}
                  </span>
                  <span style={{ padding: '3px 10px', borderRadius: 2, fontSize: '0.75rem', fontWeight: 400, background: statusC.bg, color: statusC.fg }}>
                    {statusLabel}
                  </span>
                  {p.relationship_started_at && (
                    <span style={{ padding: '3px 10px', borderRadius: 2, fontSize: '0.7rem', color: '#6E685F', background: 'transparent' }}>
                      交往中起：{String(p.relationship_started_at).slice(0, 10)}
                    </span>
                  )}
                </div>
              </div>

              {/* Tab bar — 基本资料 / 择偶要求 / 服务协议 / 跟进记录 */}
              <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid rgba(17,17,17,.12)', marginBottom: 18, flexWrap: 'wrap' }}>
                {TABS.map(tb => {
                  const on = activeTab === tb.key;
                  return (
                    <button key={tb.key} onClick={() => setActiveTab(tb.key)}
                      style={{
                        padding: '8px 14px', border: 'none', background: 'transparent', cursor: 'pointer',
                        fontSize: '0.88rem', fontWeight: on ? 700 : 500,
                        color: on ? 'var(--color-burgundy)' : '#6E685F',
                        borderBottom: on ? '2px solid var(--color-burgundy)' : '2px solid transparent',
                        marginBottom: -1, display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}>
                      {tb.icon}{tb.label}
                      {tb.key === 'followups' && followups.length > 0 && (
                        <span style={{ fontSize: 11, color: on ? 'var(--color-burgundy)' : '#6E685F', fontWeight: 400 }}>({followups.length})</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* ── 基本资料 ── */}
              {activeTab === 'basic' && (
                <>
                  {/* Bio */}
                  {p.bio && (
                    <div style={{ marginBottom: 18, padding: '14px 16px', background: '#EFEAE2', borderRadius: 2, border: '1px solid rgba(17,17,17,.12)' }}>
                      <h4 style={{ margin: '0 0 8px', fontFamily: "'Cormorant Garamond', 'Noto Serif SC', 'Songti SC', Georgia, serif", fontSize: 20, fontWeight: 500, color: 'var(--color-burgundy)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Heart size={14} /> 自我介绍
                      </h4>
                      <p style={{ margin: 0, fontSize: '0.88rem', color: '#3A3835', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{p.bio}</p>
                    </div>
                  )}

                  {/* Contact + Assignment */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : (canSeeContact ? '1fr 1fr' : '1fr'),
                    gap: 12, marginBottom: 18,
                  }}>
                    {canSeeContact && (
                      <div style={{ padding: '12px 14px', background: '#F6F3EE', borderRadius: 2 }}>
                        <h4 style={{ margin: '0 0 6px', fontSize: 11.5, fontWeight: 400, color: '#6E685F', textTransform: 'uppercase', letterSpacing: '.22em' }}>联系信息</h4>
                        <p style={{ margin: '2px 0', fontSize: '0.88rem', color: '#3A3835', display: 'flex', alignItems: 'center', gap: 6 }}><Mail size={13} /> {p.email || '-'}</p>
                        <p style={{ margin: '2px 0', fontSize: '0.88rem', color: '#3A3835', display: 'flex', alignItems: 'center', gap: 6 }}><Phone size={13} /> {p.phone || '-'}</p>
                      </div>
                    )}
                    <div style={{ padding: '12px 14px', background: '#F6F3EE', borderRadius: 2 }}>
                      <h4 style={{ margin: '0 0 6px', fontSize: 11.5, fontWeight: 400, color: '#6E685F', textTransform: 'uppercase', letterSpacing: '.22em' }}>分配</h4>
                      <p style={{ margin: '2px 0', fontSize: '0.88rem', color: '#3A3835', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <User size={13} />
                        {p.matchmaker_first_name
                          ? `${p.matchmaker_first_name} ${p.matchmaker_last_name || ''}`.trim()
                          : (p.recruiter_first_name ? `${p.recruiter_first_name} ${p.recruiter_last_name || ''}`.trim() : '-')}
                      </p>
                      <p style={{ margin: '2px 0', fontSize: '0.88rem', color: '#3A3835', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Target size={13} />
                        {p.coach_first_name
                          ? `${p.coach_first_name} ${p.coach_last_name || ''}`.trim()
                          : '-'}
                      </p>
                    </div>
                  </div>

                  {/* Custom fields grouped by category (preferences excluded) */}
                  {categoryOrder.map(cat => {
                    const items = cfGroups[cat];
                    if (!items || items.length === 0) return null;
                    return (
                      <div key={cat} style={{ marginBottom: 16 }}>
                        <h4 style={{ margin: '0 0 10px', fontFamily: "'Cormorant Garamond', 'Noto Serif SC', 'Songti SC', Georgia, serif", fontSize: 20, fontWeight: 500, color: 'var(--color-burgundy)', borderBottom: '1px solid rgba(17,17,17,.12)', paddingBottom: 6 }}>
                          {CATEGORY_LABEL_ZH[cat] || cat}
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '4px 20px' }}>
                          {items.map((item, idx) => (
                            <p key={idx} style={{ margin: '3px 0', fontSize: '0.85rem' }}>
                              <span style={{ color: '#6E685F', fontWeight: 500 }}>{item.label}:</span>{' '}
                              <span style={{ color: '#3A3835', fontWeight: 400, wordBreak: 'break-word' }}>{item.value}</span>
                            </p>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              {/* ── 择偶要求（三维度）── */}
              {activeTab === 'preferences' && (
                <div style={{ marginBottom: 8 }}>
                  <h4 style={{ margin: '0 0 12px', fontFamily: "'Cormorant Garamond', 'Noto Serif SC', 'Songti SC', Georgia, serif", fontSize: 20, fontWeight: 500, color: 'var(--color-burgundy)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Layers size={14} /> 择偶要求（理想 / 可接受 / 底线）
                  </h4>
                  {prefFields.length === 0 ? (
                    <div style={{ padding: '14px 0', color: '#6E685F', fontSize: 13 }}>暂无择偶要求</div>
                  ) : (
                    <div style={{
                      background: '#F6F3EE', border: '1px solid #EFEAE2', borderRadius: 2,
                      padding: 10, display: 'flex', flexDirection: 'column', gap: 10,
                    }}>
                      {prefFields.map((item, i) => (
                        <div key={i} style={{ background: '#FFFDF9', border: '1px solid #EFEAE2', borderRadius: 2, padding: '10px 14px' }}>
                          <div style={{ fontSize: 11.5, fontWeight: 400, letterSpacing: '.22em', color: '#8A6D3B', marginBottom: 8 }}>{item.label}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {item.rows.map((r, j) => (
                              <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.85rem' }}>
                                {r.label && (
                                  <span style={{
                                    minWidth: 52, textAlign: 'center', fontSize: 11, fontWeight: 400,
                                    padding: '2px 10px', borderRadius: 2,
                                    background: `${r.color}18`, color: r.color, border: `1px solid ${r.color}40`,
                                  }}>{r.label}</span>
                                )}
                                <span style={{ color: r.empty ? '#6E685F' : '#111111', fontWeight: r.empty ? 400 : 600, flex: 1, wordBreak: 'break-word' }}>{r.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── 服务协议 ── */}
              {activeTab === 'agreement' && (
                <div style={{ marginBottom: 8 }}>
                  <h4 style={{ margin: '0 0 12px', fontFamily: "'Cormorant Garamond', 'Noto Serif SC', 'Songti SC', Georgia, serif", fontSize: 20, fontWeight: 500, color: 'var(--color-burgundy)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <ClipboardCheck size={14} /> 服务协议
                  </h4>
                  {(() => {
                    // Signed if the client e-signed (contract_agreed via the sign
                    // link) OR a signed agreement file/date exists.
                    const unsignedAck = Number(p.info_unsigned_ack) === 1 && !p.contract_agreed;
                    const signed = !unsignedAck && !!(p.signed_agreement_url || p.contract_agreed || p.contract_signed_at || p.sign_date);
                    const signedAt = p.agreement_signed_at || p.contract_signed_at || p.sign_date;
                    if (unsignedAck) {
                      return (
                        <div style={{ padding: '10px 14px', background: 'rgba(158,43,37,.09)', border: '1px solid rgba(158,43,37,.25)', borderRadius: 2 }}>
                          <div style={{ color: '#9E2B25', fontWeight: 400, fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={14} strokeWidth={1.5} aria-hidden="true" />资料是会员提供，但未签署正式协议</div>
                          <div style={{ color: '#9E2B25', fontSize: '0.8rem', marginTop: 4 }}>
                            会员未签署正式协议，资料内容由会员本人提供。
                            {p.info_unsigned_ack_at && <> 记录时间 {fmtDate(p.info_unsigned_ack_at)}。</>}
                          </div>
                        </div>
                      );
                    }
                    return signed ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ padding: '4px 12px', background: 'rgba(63,125,90,.12)', color: '#3F7D5A', borderRadius: 2, fontSize: '0.85rem', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Check size={14} strokeWidth={1.5} aria-hidden="true" />已签字</span>
                        {p.signed_agreement_url && (
                          <a href={p.signed_agreement_url} target="_blank" rel="noopener noreferrer"
                            style={{ padding: '6px 14px', background: 'var(--color-burgundy)', color: '#FFFDF9', borderRadius: 2, textDecoration: 'none', fontSize: '0.85rem', fontWeight: 500 }}>
                            下载协议
                          </a>
                        )}
                        {signedAt && (
                          <span style={{ color: '#6E685F', fontSize: '0.82rem' }}>签署日期 {fmtDate(signedAt)}</span>
                        )}
                      </div>
                    ) : (
                      <span style={{ padding: '4px 12px', background: 'rgba(147,89,26,.10)', color: '#9E2B25', borderRadius: 2, fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}><Circle size={12} strokeWidth={1.5} aria-hidden="true" />未签署</span>
                    );
                  })()}
                </div>
              )}

              {/* ── 跟进记录 ── every follow-up note posted on a kpi-v2 ticket
                  where this profile is the client. Newest first; up to 500
                  rows. */}
              {activeTab === 'album' && (
                <PhotoAlbumTab photos={allPhotos} resolveUrl={resolvePhotoUrl} isZh
                  fileBase={[offlineId, firstName].filter(Boolean).join('-')} />
              )}

              {activeTab === 'followups' && (
                <div style={{ marginBottom: 8 }}>
                  <h4 style={{ margin: '0 0 12px', fontFamily: "'Cormorant Garamond', 'Noto Serif SC', 'Songti SC', Georgia, serif", fontSize: 20, fontWeight: 500, color: 'var(--color-burgundy)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <ClipboardList size={14} /> 跟进记录
                    {followups.length > 0 && (
                      <span style={{ fontSize: 11, color: '#6E685F', fontWeight: 500, marginLeft: 6 }}>({followups.length})</span>
                    )}
                  </h4>
                  {followupsLoading ? (
                    <div style={{ padding: '20px 0', textAlign: 'center', color: '#6E685F', fontSize: 12 }}>加载中…</div>
                  ) : followups.length === 0 ? (
                    <div style={{ padding: '14px 0', color: '#6E685F', fontSize: 13 }}>暂无跟进记录</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 460, overflowY: 'auto', paddingRight: 4 }}>
                      {followups.map(r => (
                        <div key={r.id} style={{
                          background: '#EFEAE2', border: '1px solid rgba(17,17,17,.12)', borderRadius: 2,
                          padding: '10px 12px',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11, color: '#6E685F' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#111111', fontWeight: 400 }}>
                                <User size={11} /> {followupActorName(r)}
                              </span>
                              {r.ticket_no && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 7px', background: '#EFEAE2', borderRadius: 2, color: '#3A3835', fontWeight: 400 }}>
                                  <Ticket size={10} /> {r.ticket_no}
                                </span>
                              )}
                              {r.ticket_title && <span style={{ color: '#6E685F' }}>{r.ticket_title}</span>}
                            </div>
                            <span style={{ fontSize: 11, color: '#6E685F' }}>{fmtDateTime(r.created_at)}</span>
                          </div>
                          <div style={{ fontSize: 13, lineHeight: 1.6, color: '#111111', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {r.body || <span style={{ color: '#6E685F', fontStyle: 'italic' }}>（无正文）</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Footer */}
              <div style={{ display: 'flex', gap: 10, marginTop: 'auto', paddingTop: 16, borderTop: '1px solid rgba(17,17,17,.12)' }}>
                <button onClick={onClose}
                  style={{ padding: '10px 24px', borderRadius: 2, border: '1px solid rgba(17,17,17,.12)', background: '#FFFDF9', color: '#3A3835', fontWeight: 400, cursor: 'pointer', fontSize: '0.88rem' }}>
                  关闭
                </button>
                {canEdit && (
                  <button onClick={() => { onClose?.(); navigate(`/profiles/${profileId}/edit`); }}
                    style={{ padding: '10px 24px', borderRadius: 2, border: 'none', background: 'var(--color-burgundy)', color: '#FFFDF9', fontWeight: 400, cursor: 'pointer', fontSize: '0.88rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Edit2 size={14} /> 编辑
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
